import React, { useState } from 'react'
import { Menu, Popover, useMenuContext } from '@chakra-ui/react'
import { Button, Stack, Box, Text, Textarea, SimplePopover, SubMenu } from '@/components/lib'

// The shortest reason the API accepts for a deletion (util/trials.py
// MIN_DELETION_REASON_LENGTH); the button stays disabled until it is met so
// the request is never made only to be refused.
export const MIN_REASON_LENGTH = 4

export default function ConfirmButton ({ header, body, footer, trigger, onConfirm, action, actionColor, closeOnBlur, asSubMenu, reasonRequired, reasonLabel }) {
  // When used inside a menu, this is the menu the trigger item lives in (the
  // submenu itself is opened below). Undefined when not rendered in a menu.
  const parentMenu = useMenuContext()

  // With `reasonRequired`, the confirmation carries a free-text reason that is
  // passed to onConfirm and recorded in the audit log. Kept here rather than
  // in each caller so every delete asks in the same way.
  const [reason, setReason] = useState('')
  const reasonOk = !reasonRequired || reason.trim().length >= MIN_REASON_LENGTH

  // Always dismiss before acting: the action often removes the item the button
  // belongs to, and a still-open popover/menu then reattaches to whatever takes
  // its place in the list, inviting a second, accidental confirmation.
  const confirm = (dismiss) => () => {
    dismiss()
    const given = reason.trim()
    setReason('')
    if (onConfirm) onConfirm(reasonRequired ? given : undefined)
    if (action) action()
  }

  const reasonField = reasonRequired && (
    <Stack gap={1}>
      <Text fontSize='sm' fontWeight='bold'>{reasonLabel || 'Reason'}</Text>
      <Textarea
        size='sm'
        rows={2}
        value={reason}
        placeholder='Explain why this is being deleted (recorded in the event log)'
        onChange={e => setReason(e.target.value)}
      />
    </Stack>
  )

  if (asSubMenu) {
    return (
      <SubMenu trigger={trigger}>
        <Menu.Context>
          {menu => (
            <Stack p={3}>
              {header && <Box fontWeight='bold'>{header}</Box>}
              {body && <Box>{body}</Box>}
              {reasonField}
              <Stack direction='row' justifyContent='end'>
                <Button
                  disabled={!reasonOk}
                  onClick={confirm(() => {
                    menu.setOpen(false)
                    if (parentMenu) parentMenu.setOpen(false)
                  })} colorPalette={actionColor || 'gray'}
                >Confirm
                </Button>
              </Stack>
              {footer && <Box textAlign='right'>{footer}</Box>}
            </Stack>
          )}
        </Menu.Context>
      </SubMenu>
    )
  }
  return (
    <SimplePopover
      closeOnBlur={closeOnBlur ?? true}
      trigger={trigger}
      header={header}
      body={
        <Popover.Context>
          {popover => (
            <Stack>
              {body && <Box>{body}</Box>}
              {reasonField}
              <Stack direction='row' justifyContent='end'>
                <Button
                  disabled={!reasonOk}
                  onClick={confirm(() => popover.setOpen(false))} colorPalette={actionColor || 'gray'}
                >Confirm
                </Button>
              </Stack>
            </Stack>
          )}
        </Popover.Context>
}
      footer={<Box textAlign='right'>{footer}</Box>}
    />
  )
}
